import React, { useState, useEffect, useCallback } from 'react';
import { makeClient } from './scheduleClient';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Shield, ShieldCheck, MapPin, Plus, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/schedule`;
const axios = makeClient();

export default function ProfilePage({ go }) {
  const { user: hubUser } = useAuth();
  const [host, setHost] = useState(null);
  const [myRoles, setMyRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venueServices, setVenueServices] = useState({});
  const [employees, setEmployees] = useState([]);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);

  // Secondary sign-up form state
  const [selectedVenue, setSelectedVenue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    if (hubUser) setHost({ id: (hubUser._id || hubUser.id), name: hubUser.name, email: hubUser.email });
  }, [hubUser]);

  const fetchData = useCallback(async () => {
    if (!host) return;
    setLoading(true);
    try {
      const [allRolesRes, venuesRes, servicesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/venue-roles`),
        axios.get(`${API}/venues`),
        axios.get(`${API}/venue-roles/services`),
        axios.get(`${API}/hosts`)
      ]);
      setMyRoles(allRolesRes.data.filter(r => r.employee_id === host.id));
      setAllRoles(allRolesRes.data);
      setVenues(venuesRes.data);
      setVenueServices(servicesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  }, [host]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getVenueName = (venueId) => venues.find(v => v.id === venueId)?.name || 'Unknown';
  const getEmployeeName = (employeeId) => employees.find(e => e.id === employeeId)?.name || 'Unknown';

  const handleSignUpSecondary = async () => {
    if (!selectedVenue || !selectedCategory) {
      toast.error('Please select a venue and category');
      return;
    }
    try {
      await axios.post(`${API}/venue-roles`, {
        venue_id: selectedVenue,
        employee_id: host.id,
        role_category: selectedCategory,
        role_type: 'secondary'
      });
      toast.success('Signed up as secondary!');
      setSelectedVenue('');
      setSelectedCategory('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up');
    }
  };

  const handleRemoveRole = async (roleId) => {
    try {
      await axios.delete(`${API}/venue-roles/${roleId}`);
      toast.success('Role removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove role');
    }
  };

  const myPrimaryRoles = myRoles.filter(r => r.role_type === 'primary');
  const mySecondaryRoles = myRoles.filter(r => r.role_type === 'secondary');

  // Available venues for secondary sign-up (exclude venues where user already has this role)
  const getAvailableVenuesForCategory = (category) => {
    const existingVenueIds = myRoles
      .filter(r => r.role_category === category && r.role_type === 'secondary')
      .map(r => r.venue_id);
    return Object.values(venueServices)
      .filter(vs => {
        if (category === 'trivia') return vs.offers_trivia;
        if (category === 'bingo_karaoke') return vs.offers_bingo_karaoke;
        return false;
      })
      .filter(vs => !existingVenueIds.includes(vs.venue_id));
  };

  // Get available categories for selected venue
  const getAvailableCategoriesForVenue = (venueId) => {
    const vs = venueServices[venueId];
    if (!vs) return [];
    const categories = [];
    if (vs.offers_trivia) {
      const alreadyHas = myRoles.some(r => r.venue_id === venueId && r.role_category === 'trivia' && r.role_type === 'secondary');
      if (!alreadyHas) categories.push({ value: 'trivia', label: 'Trivia' });
    }
    if (vs.offers_bingo_karaoke) {
      const alreadyHas = myRoles.some(r => r.venue_id === venueId && r.role_category === 'bingo_karaoke' && r.role_type === 'secondary');
      if (!alreadyHas) categories.push({ value: 'bingo_karaoke', label: 'Bingo/Karaoke' });
    }
    return categories;
  };

  // Venues available for secondary sign-up
  const signUpVenues = Object.values(venueServices).filter(vs => {
    const cats = getAvailableCategoriesForVenue(vs.venue_id);
    return cats.length > 0;
  });

  // For each venue, show who is primary/secondary
  const getRolesForVenue = (venueId, category) => {
    return allRoles.filter(r => r.venue_id === venueId && r.role_category === category);
  };

  if (loading || !host) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary"></div>
      </div>
    );
  }

  const categoryLabel = (cat) => cat === 'trivia' ? 'Trivia' : 'Bingo/Karaoke';

  return (
    <div data-testid="profile-page">
      {/* Header */}
      <header className="bg-white/5 border-b border-border shadow-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button onClick={() => go('schedule')} variant="ghost" size="icon" data-testid="profile-back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="p-1">
                <img src="/hat-logo.png" alt="BIG Hat Entertainment" className="h-10 w-10 object-contain" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">My Profile</h1>
                <p className="text-sm text-muted-foreground">{host.name} &middot; {host.email}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Primary Roles Info */}
        {myPrimaryRoles.length > 0 && (
          <Card className="border-green-400 bg-green-50" data-testid="validation-success">
            <CardContent className="py-4 flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <p className="font-semibold text-green-800">Primary roles assigned. All other hosts are automatic backups for your events.</p>
            </CardContent>
          </Card>
        )}

        {/* My Primary Roles */}
        <Card data-testid="primary-roles-section">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              <span>My Primary Roles</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myPrimaryRoles.length === 0 ? (
              <p className="text-muted-foreground text-sm">You are not assigned as a primary at any venue. Admins assign primary roles.</p>
            ) : (
              <div className="space-y-3">
                {myPrimaryRoles.map(role => (
                  <div key={role.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid={`primary-role-${role.id}`}>
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="font-medium text-foreground">{getVenueName(role.venue_id)}</p>
                        <Badge variant="outline" className="text-xs mt-1">{categoryLabel(role.role_category)}</Badge>
                      </div>
                    </div>
                    <Badge className="bg-blue-600 text-white">Primary</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Backup Status */}
        <Card data-testid="secondary-roles-section">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-green-600" />
              <span>Backup Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800 font-medium">You are automatically a backup for all events you are not assigned as primary.</p>
              <p className="text-xs text-green-600 mt-1">When an event is unclaimed by Monday, you will be notified and can claim it.</p>
            </div>
          </CardContent>
        </Card>

        {/* All Venue Roles Overview */}
        <Card data-testid="venue-roles-overview">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="h-5 w-5 text-purple-600" />
              <span>All Venue Roles</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {Object.values(venueServices).map(vs => {
                const triviaRoles = getRolesForVenue(vs.venue_id, 'trivia');
                const bkRoles = getRolesForVenue(vs.venue_id, 'bingo_karaoke');
                const triviaPrimary = triviaRoles.find(r => r.role_type === 'primary');
                const bkPrimary = bkRoles.find(r => r.role_type === 'primary');

                return (
                  <div key={vs.venue_id} className="p-4 rounded-lg border border-border bg-white/5" data-testid={`venue-overview-${vs.venue_id}`}>
                    <h4 className="font-semibold text-lg mb-3">{vs.venue_name}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {vs.offers_trivia && (
                        <div className="p-3 rounded-md bg-green-50 border border-green-200">
                          <p className="text-sm font-semibold text-green-800 mb-2">Trivia</p>
                          <p className="text-sm text-green-700">
                            <span className="font-medium">Primary:</span>{' '}
                            {triviaPrimary ? getEmployeeName(triviaPrimary.employee_id) : <span className="italic text-slate-500">Unassigned</span>}
                          </p>
                          <p className="text-xs text-green-600 mt-1 italic">All other hosts are automatic backups</p>
                        </div>
                      )}
                      {vs.offers_bingo_karaoke && (
                        <div className="p-3 rounded-md bg-pink-50 border border-pink-200">
                          <p className="text-sm font-semibold text-pink-800 mb-2">Bingo/Karaoke</p>
                          <p className="text-sm text-pink-700">
                            <span className="font-medium">Primary:</span>{' '}
                            {bkPrimary ? getEmployeeName(bkPrimary.employee_id) : <span className="italic text-slate-500">Unassigned</span>}
                          </p>
                          <p className="text-xs text-pink-600 mt-1 italic">All other hosts are automatic backups</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {Object.keys(venueServices).length === 0 && (
                <p className="text-muted-foreground text-sm">No venues have pricing configured yet. Ask an admin to set up venue pricing.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
